import { Theme, createStyles, withStyles, IconButton } from "@material-ui/core";
import { KeyDownMessage } from "meet-addons-sdk";
import { observe } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { MeetingSnapshot } from "../../domain/api/meetings/MeetingSnapshot";
import { MeetingSnapshotSentiment } from "../../domain/api/meetings/MeetingSnapshotSentiment";
import meetingSnapshotDataService from "../../services/api/MeetingSnapshotDataService";
import i18n from "../../services/localization/i18n";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import AppIcon from "../shared/AppIcon";
import { AppIconType } from "../shared/AppIconType";

const styles = (theme: Theme) => createStyles({
    button: {
        height: theme.spacing(5),
        width: theme.spacing(5),
        marginRight: theme.spacing() 
    },
    negativeSelected: {
        borderColor: theme.palette.error.main,
        borderWidth: 2,
        borderStyle: "solid",
        height: theme.spacing(5),
        width: theme.spacing(5),
        marginRight: theme.spacing()
    },
    root: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        height: theme.spacing(7)
    },
    selected: {
        height: theme.spacing(5),
        width: theme.spacing(5),
        borderColor: theme.palette.secondary.main,
        borderWidth: 2,
        borderStyle: "solid",
        marginRight: theme.spacing()
    }
});

interface ISnapshotSentimentProps {
    classes: {
        button: string;
        negativeSelected: string;
        root: string;
        selected: string;
    };
    snapshot: MeetingSnapshot;
    selected: boolean;
}

interface ISnapshotCardState {
    hasNegativeSentiment: boolean;
    hasPositiveSentiment: boolean;
    sentiment?: MeetingSnapshotSentiment;
}

@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.CONFIG)
@observer
class SnapshotSentiment extends React.Component<ISnapshotSentimentProps & IConfigStoreProps & IContextStoreProps, ISnapshotCardState> {

    /**
     * Creates an instance of SnapshotSentiment.
     * @param {(ISnapshotSentimentProps & IConfigStoreProps)} props
     * @memberof SnapshotSentiment
     */
    constructor(props: ISnapshotSentimentProps & IConfigStoreProps & IContextStoreProps) {
        super(props);

        const { me } = props.configStore!;
        const sentiment = props.snapshot.sentiments.find(s => s.userId === me!.id!);

        this.state = {
            sentiment: sentiment,
            hasNegativeSentiment: sentiment?.sentiment ? sentiment.sentiment < 50 : false,
            hasPositiveSentiment: sentiment?.sentiment ? sentiment.sentiment > 50 : false,
        }

        observe(this.props.contextStore!, "keyDownMessage", (change) => this.handleKeyDown(change.newValue!));
    }

    public render() {
        
        const { button, negativeSelected, selected, root } = this.props.classes;

        const { hasNegativeSentiment, hasPositiveSentiment } = this.state;
        
        return <div className={root}>
            <IconButton className={hasPositiveSentiment ? selected : button }
                        title = { i18n.t("snapshot_sentiment_positive") }
                        onClick={this.onPositiveSentiment}>
                <AppIcon icon={AppIconType.ThumbUp} />
            </IconButton>

            <IconButton className={hasNegativeSentiment ? negativeSelected : button }
                        title = { i18n.t("snapshot_sentiment_negative") }
                        onClick={this.onNegativeSentiment}>
                <AppIcon icon={AppIconType.ThumbDown} />
            </IconButton>
        </div>;
    }

    private onPositiveSentiment = () => {


        const { id, partitionKey } = this.props.snapshot;
        const { me } = this.props.configStore!;
        const { addOrUpdateSnapshot } = this.props.contextStore!;
        const { hasPositiveSentiment } = this.state;

        const value = hasPositiveSentiment ? undefined : 100;
        const sentiment = {
            userId: me!.id,
            partitionKey, 
            sentiment: value,
        } as MeetingSnapshotSentiment;

        meetingSnapshotDataService.recordSentimentAsync(id!, sentiment)
            .then(snapshot => {
                addOrUpdateSnapshot(snapshot);
                this.setState({ 
                    hasNegativeSentiment: false,
                    hasPositiveSentiment: value === 100,
                });
            });
    }

    private onNegativeSentiment = () => {
        const { id, partitionKey } = this.props.snapshot;
        const { me } = this.props.configStore!;
        const { addOrUpdateSnapshot } = this.props.contextStore!;

        const { hasNegativeSentiment } = this.state;
        const value = hasNegativeSentiment ? undefined : 1;
        const sentiment = {
            userId: me!.id,
            partitionKey, 
            note: this.state.sentiment?.note,
            sentiment: value,
        } as MeetingSnapshotSentiment;

        meetingSnapshotDataService.recordSentimentAsync(id!, sentiment)
            .then(snapshot => {
                addOrUpdateSnapshot(snapshot);
                this.setState({ 
                    hasNegativeSentiment: value === 1,
                    hasPositiveSentiment: false,
                });
            });
    }

    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement> | KeyDownMessage) => {
        if (!this.props.selected) {
            return;
        }
        switch (e.keyCode) {
            case 107: // num +
            case 187: // +
                this.onPositiveSentiment();
                break;
            case 109: // num -
            case 189: // -
                this.onNegativeSentiment();
                break;
        }
    }
    
}

export default withStyles(styles)(SnapshotSentiment);